import { Component, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { IdeSurfaceTokens, ProductThemeSelection, ThemeService } from './service/theme/theme.service';

@Component( {
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: [ './app.component.scss' ]
} )
export class AppComponent {

  darkLightSetting: string;
  themeColorSetting: string;
  themeDesignSetting: string;
  productTheme: ProductThemeSelection | null = null;
  codigrateSurface: IdeSurfaceTokens | null = null;

  constructor(
    private themeService: ThemeService,
    private router: Router,
    @Inject( DOCUMENT ) private doc: Document
  ) {

    // Jump back to the top on every page navigation, for every layout (landing,
    // tools/plugins, splash). The page scrolls on <body> (not the window), so
    // Angular's scrollPositionRestoration can't do it. Two opt-outs: anchor
    // navigations (url with a #fragment, e.g. an in-page service link) still
    // scroll to their target, and in-page URL updates that navigate with
    // `state: { scrollToTop: false }` (e.g. the palette tool's Generate) keep
    // the reader where they are.
    let skipScrollToTop = false;
    this.router.events.subscribe( e => {
      if ( e instanceof NavigationStart ) {
        skipScrollToTop =
          this.router.getCurrentNavigation()?.extras?.state?.[ 'scrollToTop' ] === false;
        return;
      }
      if ( !( e instanceof NavigationEnd ) ) {
        return;
      }
      if ( skipScrollToTop || e.urlAfterRedirects.includes( '#' ) ) {
        return;
      }
      const toTop = () => {
        this.doc.body.scrollTop = 0;
        this.doc.documentElement.scrollTop = 0;
        window.scrollTo( 0, 0 );
      };
      toTop();
      // Re-apply after the new view paints. On iOS Safari the body scroll
      // container's content height isn't settled at NavigationEnd, so the
      // first reset can be a no-op until the new page has laid out.
      window.requestAnimationFrame( toTop );
    } );

    this.themeService.darkLightSetting.subscribe( darkLightSetting => {
      this.darkLightSetting = darkLightSetting;
      this.syncThemeColorMeta();
    } );

    this.themeService.productTheme.subscribe( productTheme => {
      this.productTheme = productTheme;
    } );

    this.themeService.productSurface.subscribe( codigrateSurface => {
      this.codigrateSurface = codigrateSurface;
      this.syncThemeColorMeta();
    } );

    this.themeService.themeColorSetting.subscribe( themeColorSetting => {
      this.themeColorSetting = themeColorSetting;
      this.syncThemeColorMeta();
    } );

    this.themeService.themeDesignSetting.subscribe( themeDesignSetting => {
      this.themeDesignSetting = themeDesignSetting;
    } );
  }

  // Keep <meta name="theme-color"> (the iOS Safari status bar tint, on browsers
  // that honour it) matching the top bar's WINDOW background, so the status bar
  // blends with the themed top bar. Paint that same window background onto
  // <html> only, the rubber-band overscroll reveals the root (<html>), so the
  // gap above the top bar / below the bottom nav fills with the window tone that
  // both bars use. <body> is deliberately left to its CSS `var(--background-color)`
  // (the EDITOR background): body is the page backdrop the cards melt into, so it
  // must stay editor-toned, not window-toned (painting it window-toned made the
  // card's editor-background bottom edge stand out instead of melting, most
  // visibly on the default theme where window and editor differ more than on the
  // near-monochrome IDE themes). The skin's real window background only resolves
  // inside .codigrate-skin, so we read it back from #app-component rather than a
  // document-root token. Deferred a frame so theme bindings have applied first.
  private syncThemeColorMeta(): void {
    window.requestAnimationFrame( () => {
      const appEl = this.doc.getElementById( 'app-component' );
      const background = appEl ? window.getComputedStyle( appEl ).backgroundColor : '';
      if ( !background || background === 'rgba(0, 0, 0, 0)' || background === 'transparent' ) {
        return;
      }
      let meta = this.doc.querySelector<HTMLMetaElement>( 'meta[name="theme-color"]' );
      if ( !meta ) {
        meta = this.doc.createElement( 'meta' );
        meta.setAttribute( 'name', 'theme-color' );
        this.doc.head.appendChild( meta );
      }
      meta.setAttribute( 'content', background );

      // <html> = window background (covers the overscroll gap); <body> stays editor.
      this.doc.documentElement.style.backgroundColor = background;
    } );
  }

  setTheme() {
    let themeConfig = 'default-theme';

    if ( this.themeColorSetting ) {
      themeConfig = this.themeColorSetting;
    }

    if ( this.themeDesignSetting ) {
      themeConfig += ' ' + this.themeDesignSetting;
    }

    return themeConfig;
  }
}
